import { useCopilotAction } from '@copilotkit/react-core'

import { useActivities } from 'modules/core/lib/activities'
import { getActivity, type ActivityCatalog } from 'modules/core/lib/activities/catalog'
import type { SerializableProps } from 'modules/core/lib/activities/types'

function isRecordOfUnknown(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function isSerializableProps(value: unknown): value is SerializableProps {
  return isRecordOfUnknown(value)
}

function parseActivityData(data: unknown): SerializableProps | undefined {
  if (!isSerializableProps(data) || Object.keys(data).length === 0) return undefined
  return data
}

/**
 * Validate activity id from Copilot (string) and return ActivityCatalog when
 * the activity exists.
 */
function parseActivityId(id: string): ActivityCatalog | null {
  // ActivityCatalog is a union of string literals; we must assert after
  // getActivity validates the existence.
  // eslint-disable-next-line no-type-assertion/no-type-assertion -- Copilot passes string; catalog is typed as literal union
  const catalogId = id as ActivityCatalog
  return getActivity(catalogId) ? catalogId : null
}

/**
 * Open an in-app activity (overlay/modal) by catalog id.
 */
export const useOpenActivityAction = () => {
  const { openActivity } = useActivities()

  useCopilotAction({
    name: 'openActivity',
    available: 'remote',
    followUp: false,
    description:
      'Open an in-app activity (overlay/modal). Use when the user asks to open a specific overlay, e.g. team status by clock-in state, company links edit, or other activities. Activity IDs are dot-separated, e.g. core.dashboardTeamStatus (takes data: { status: "should_not_be_working" | "missing_clock_in" | "working" }), core.dashboardCompanyLinksEdit.',
    parameters: [
      {
        name: 'activityId',
        description:
          'The activity ID (e.g. core.dashboardTeamStatus, core.dashboardCompanyLinksEdit).',
        required: true,
      },
      {
        name: 'data',
        description:
          'Optional JSON-serializable data for the activity. For core.dashboardTeamStatus use { status: "should_not_be_working" | "missing_clock_in" | "working" }. For richer previews, include the data the activity should render instead of expecting the frontend to look it up again.',
        required: false,
      },
    ],
    handler: async ({ activityId, data }) => {
      const catalogId = parseActivityId(activityId)
      if (!catalogId) return `Unknown activity: ${activityId}.`
      const params = parseActivityData(data)
      openActivity(catalogId, params)
    },
  })
}
